import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { usePortalFocus, useScrollToFocus } from "@/hooks/usePortalFocus";
import { AlertTriangle, Archive, Download, FileUp, Mail, Paperclip, Pencil, Printer, Receipt, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import { escapePrintHtml } from "@/lib/clerkStatements";
import { isPdfFile, statementFileUrl } from "@/lib/feeSlipStatements";
import { fetchStoredFile } from "@/lib/storedFile";
import { useDisbursements } from "@/hooks/useDisbursements";
import { useClaimReceipts } from "@/hooks/useClaimReceipts";
import ClaimReceiptsFolder from "@/components/claims/ClaimReceiptsFolder";
import { awaitingProviderPayment } from "@/lib/submittedInvoices";
import { addReceipt, buildReceipt, receiptsFor } from "@/lib/claimReceipts";
import FinanceEmailPanel from "@/components/disbursements/FinanceEmailPanel";
import PartialPaidSplit from "@/components/PartialPaidSplit";
import { partialSplit } from "@/lib/partialPaid";
import { EMAIL_INTAKE_BRANDS, fetchEmailIntake, senderName, updateEmailIntake } from "@/lib/emailIntake";
import {
  DISBURSEMENT_STATUSES,
  DISBURSEMENT_TYPES,
  REIMBURSEMENT_LABEL,
  addDisbursements,
  amountOwing,
  buildDisbursement,
  disbursementStatusLabel,
  disbursementStatusText,
  statusesFor,
  disbursementTypeLabel,
  emailDisbursementCandidates,
  findDuplicate,
  isPaidDisbursement,
  legacyDisbursementCandidates,
  parseAmount,
  removeDisbursement,
  setIgnored,
  showsReimbursement,
  splitPdfIntoInvoices,
  summariseDisbursements,
  updateDisbursement,
} from "@/lib/disbursements";

// Disbursements: outside invoices on a client's matter that are not counsel's fees -
// psychological, psychiatric and medical reports, interpreters, transcripts. Upload the
// supplier's PDF (one invoice, or several one to a page), check what was read, then track
// each invoice to paid. Replaces the old Monthly Trust Receipts screen for LACW, VALS and
// EAS; RGA keeps trust receipts. See src/lib/disbursements.js.
//
// An administrator also sees "To bring in": disbursement invoices that arrived by email
// (filed by the email intake, still unsorted) and the old Client Account's disbursement
// records. Each goes through the same check before it is saved, and an email brought in is
// re-filed as a disbursement so it leaves the Fee Slips screen's "check type" list.

// How long to wait for the old Client Account's records: Deadline Guard can be slow to wake.
const LEGACY_TIMEOUT_MS = 30000;

const BRANDS = {
  lacw: { orgName: "Law & Advocacy Centre for Women", prefix: "LACW", button: "bg-teal-700 hover:bg-teal-800", icon: "text-teal-700" },
  vals: { orgName: "Victorian Aboriginal Legal Service", prefix: "VALS", button: "bg-rose-700 hover:bg-rose-800", icon: "text-rose-700" },
  eas: { orgName: "EAS Legal", prefix: "EAS", button: "bg-amber-700 hover:bg-amber-800", icon: "text-amber-800" },
  rga: { orgName: "Robyn Greensill & Associates", prefix: "RGA", button: "bg-indigo-700 hover:bg-indigo-800", icon: "text-indigo-700" },
};

const money = (value) => (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const day = (value) => {
  const [y, m, d] = String(value || "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

const STATUS_STYLE = {
  not_paid: "border-slate-300 bg-white text-slate-800",
  aid_pending: "border-sky-200 bg-sky-50 text-sky-800",
  retrospective_aid_required: "border-amber-200 bg-amber-50 text-amber-800",
  aid_not_applied_for: "border-orange-200 bg-orange-50 text-orange-800",
  to_be_paid_from_budget: "border-rose-200 bg-rose-50 text-rose-800",
  claim_submitted: "border-blue-200 bg-blue-50 text-blue-800",
  finance_emailed: "border-indigo-200 bg-indigo-50 text-indigo-800",
  claimed_to_provider: "border-teal-200 bg-teal-50 text-teal-800",
  claimed_to_vals: "border-cyan-200 bg-cyan-50 text-cyan-800",
  partial_paid: "border-violet-200 bg-violet-50 text-violet-800",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-800",
  other: "border-slate-200 bg-slate-50 text-slate-700",
};

// The coloured left edge of each invoice's card, so the status reads at a glance and one
// invoice is not mistaken for the next.
const STATUS_EDGE = {
  not_paid: "border-l-slate-400",
  aid_pending: "border-l-sky-400",
  retrospective_aid_required: "border-l-amber-400",
  aid_not_applied_for: "border-l-orange-400",
  to_be_paid_from_budget: "border-l-rose-400",
  claim_submitted: "border-l-blue-400",
  finance_emailed: "border-l-indigo-400",
  claimed_to_provider: "border-l-teal-400",
  claimed_to_vals: "border-l-cyan-400",
  partial_paid: "border-l-violet-400",
  paid: "border-l-emerald-500",
  other: "border-l-slate-300",
};

const readDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(reader.error || new Error("The file could not be read"));
  reader.readAsDataURL(file);
});

async function readPdfPages(file) {
  // Loaded only when someone uploads, so the PDF reader does not weigh down the screen.
  const { extractPdfPagesWithPdfJs } = await import("@/lib/standaloneServices");
  return extractPdfPagesWithPdfJs(await readDataUrl(file));
}

// Ready to save: the check step needs at least a client and an amount.
const isComplete = (item) => Boolean(String(item.clientName || "").trim()) && parseAmount(item.amount) > 0;

const fileLink = (item) => {
  const url = statementFileUrl({ fileUrl: item.fileUrl });
  if (!url || url.startsWith("data:")) return url;
  return item.page > 1 ? `${url}#page=${item.page}` : url;
};

const EDIT_KEYS = ["type", "typeOther", "clientName", "supplier", "invoiceRef", "invoiceDate", "description", "amount", "gst", "aidRef", "status", "paidOn", "vlaClaimed", "budgetRemaining", "note"];

function TypeSelect({ value, onChange, id }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="mt-1 h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        {DISBURSEMENT_TYPES.map((type) => (
          <SelectItem key={type.value} value={type.value} className="text-xs">{type.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// `item` (optional) lets the shown status carry its date; `brand` picks the statuses offered.
function StatusSelect({ value, onChange, id, className = "", disabled, label, brand, item }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} aria-label={label} className={`h-8 text-[11px] ${STATUS_STYLE[value] || ""} ${className}`}>
        <SelectValue>{item && item.status === value ? disbursementStatusText(item) : disbursementStatusLabel(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {statusesFor(brand).map((status) => (
          <SelectItem key={status.value} value={status.value} className="text-xs">{status.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// The fields of one invoice, used both to check an upload and to edit a saved invoice.
function InvoiceFields({ idPrefix, values, onChange, reimbursement, brand }) {
  const set = (key) => (event) => onChange({ [key]: event.target.value });
  const field = (key, label, props = {}) => (
    <div>
      <Label htmlFor={`${idPrefix}-${key}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</Label>
      <Input id={`${idPrefix}-${key}`} value={values[key] ?? ""} onChange={set(key)} className="mt-1 h-8 text-xs bg-white" {...props} />
    </div>
  );
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <Label htmlFor={`${idPrefix}-type`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Type</Label>
        <TypeSelect id={`${idPrefix}-type`} value={values.type} onChange={(type) => onChange({ type })} />
      </div>
      {values.type === "other" && field("typeOther", "Type name (e.g. Pharmacy records)")}
      {field("clientName", "Client")}
      {field("supplier", "Supplier")}
      {field("invoiceRef", "Invoice number")}
      {field("invoiceDate", "Invoice date", { type: "date" })}
      {field("amount", "Amount (incl. GST)", { inputMode: "decimal" })}
      {field("gst", "GST included", { inputMode: "decimal" })}
      <div className="sm:col-span-2">{field("description", "Description")}</div>
      {field("aidRef", "Aid / file number")}
      <div>
        <Label htmlFor={`${idPrefix}-status`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Status</Label>
        <StatusSelect id={`${idPrefix}-status`} value={values.status} onChange={(status) => onChange({ status })} className="mt-1" brand={brand} />
      </div>
      {(values.status === "paid" || values.status === "partial_paid") && field("paidOn", "Date paid", { type: "date" })}
      {values.status === "partial_paid" && field("vlaClaimed", "Claimed from VLA", { inputMode: "decimal" })}
      {values.status === "partial_paid" && field("budgetRemaining", "Remaining to pay from budget", {
        inputMode: "decimal",
        placeholder: (() => {
          const split = partialSplit(values.amount, values.vlaClaimed, "");
          return split.fromBudget !== null ? split.fromBudget.toFixed(2) : "";
        })(),
      })}
      <div className="sm:col-span-2">{field("note", "Note")}</div>
      {reimbursement && (
        <label className="flex items-center gap-2 self-end pb-1.5 text-xs font-semibold text-amber-800">
          <input
            type="checkbox"
            checked={values.reimburse === true}
            onChange={(event) => onChange({ reimburse: event.target.checked })}
            className="h-4 w-4 accent-amber-600"
          />
          {REIMBURSEMENT_LABEL}
        </label>
      )}
    </div>
  );
}

// `readOnly`: LACW's own staff see their disbursements and what has been paid, but only an
// administrator changes them (Emma, 30 Oct 2026).
export default function DisbursementsTracker({ brandType = "lacw", readOnly = false }) {
  const { user } = useAuth();
  const brand = BRANDS[brandType] || BRANDS.lacw;
  const userName = user?.full_name || user?.name || user?.email || "";
  const isAdmin = user?.role === "admin";
  // VALS Finance pays the providers once a disbursement is claimed in ATLAS, so VALS's page
  // can email them from each invoice (src/lib/disbursementEmail.js).
  const emailsFinance = brandType === "vals" && isAdmin && !readOnly;
  const signOff = String(user?.full_name || user?.name || "").trim().split(/\s+/)[0] || "Emma";
  const reimbursement = showsReimbursement(brandType);
  const store = useDisbursements(brandType, `${brand.prefix} Disbursements`);
  const { data, loaded, loadError, saving, change } = store;
  const summary = useMemo(() => summariseDisbursements(data.items), [data.items]);
  // ATLAS claim receipts, kept against disbursements; the same folder as the Fee Slips page's
  // (src/lib/claimReceipts.js, Emma 26 Oct 2026).
  const claimReceipts = useClaimReceipts(brandType, brand.prefix);
  const claimCandidates = useMemo(() => data.items.map((item) => ({
    kind: "disbursement",
    id: item.id,
    label: [item.clientName, item.supplier, item.invoiceRef && `invoice ${item.invoiceRef}`, item.amount ? money(item.amount) : ""].filter(Boolean).join(" · "),
    clientName: item.clientName,
    invoiceRef: item.invoiceRef,
    amount: Number(item.amount) || 0,
  })), [data.items]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [reading, setReading] = useState(false);
  // Invoices read from the chosen files, waiting to be checked: { key, file, include, ...fields }.
  const [pending, setPending] = useState([]);
  const [savingUpload, setSavingUpload] = useState(false);
  // Links to files chosen from the computer, made once per file and let go when the file
  // leaves the check step.
  const localFileUrls = useRef(new Map());
  useEffect(() => {
    const still = new Set(pending.map((item) => item.file).filter(Boolean));
    for (const [file, url] of localFileUrls.current) {
      if (!still.has(file)) {
        URL.revokeObjectURL(url);
        localFileUrls.current.delete(file);
      }
    }
  }, [pending]);
  useEffect(() => () => {
    for (const url of localFileUrls.current.values()) URL.revokeObjectURL(url);
    localFileUrls.current.clear();
  }, []);
  // Where to open an invoice being checked: an emailed PDF from storage (at its page), or the
  // file just chosen from the computer.
  const pendingFileLink = (item) => {
    if (item.file) {
      if (!localFileUrls.current.has(item.file)) localFileUrls.current.set(item.file, URL.createObjectURL(item.file));
      const url = localFileUrls.current.get(item.file);
      return item.page > 1 ? `${url}#page=${item.page}` : url;
    }
    return fileLink({ fileUrl: item.fileUrl, page: item.page });
  };

  const [statusFilter, setStatusFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("all");
  const payProvider = useMemo(() => awaitingProviderPayment(data.items), [data.items]);
  const [reimburseOnly, setReimburseOnly] = useState(false);
  const [search, setSearch] = useState("");

  // A disbursement opened from the morning summary: the list shows what it is filed under
  // (not yet paid, the provider still to pay, or everything), then scrolls to it.
  const focus = usePortalFocus("disbursement", brandType);
  const [focusShown, setFocusShown] = useState(false);
  useEffect(() => {
    if (!focus || focusShown || !loaded) return;
    const item = data.items.find((entry) => entry.id === focus.id);
    setFocusShown(true);
    if (!item) return;
    setStatusFilter(awaitingProviderPayment([item]).length ? "pay_provider" : isPaidDisbursement(item) ? "all" : "open");
    setTypeFilter("all");
    setReimburseOnly(false);
    setSearch("");
  }, [focus, focusShown, loaded, data.items]);
  useScrollToFocus(focus?.id, focusShown);
  const [editing, setEditing] = useState(null); // { id, values, file }
  const [savingEdit, setSavingEdit] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [emailingId, setEmailingId] = useState(null);

  // ---- To bring in: emailed invoices and the old Client Account (administrators only) ----

  const [emailItems, setEmailItems] = useState([]);
  const [emailState, setEmailState] = useState("idle"); // idle | loading | ready | error
  const [legacyRecords, setLegacyRecords] = useState([]);
  const [legacyState, setLegacyState] = useState("idle");
  const [showOtherFiles, setShowOtherFiles] = useState(false);
  const [bringingIn, setBringingIn] = useState("");
  const [readingProgress, setReadingProgress] = useState("");
  // Emailed invoices ticked to add together, by candidate key.
  const [selectedEmails, setSelectedEmails] = useState(() => new Set());

  const loadEmails = useCallback(async () => {
    if (!isAdmin || !EMAIL_INTAKE_BRANDS.includes(brandType)) return;
    setEmailState("loading");
    try {
      setEmailItems(await fetchEmailIntake(brandType));
      setEmailState("ready");
    } catch (err) {
      console.warn("Emailed items could not be loaded:", err);
      setEmailState("error");
    }
  }, [isAdmin, brandType]);

  // Loaded once when the page opens - nothing here refreshes on a timer.
  useEffect(() => { loadEmails(); }, [loadEmails]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let left = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LEGACY_TIMEOUT_MS);
    setLegacyState("loading");
    fetch("/api/render/api/disbursements/records?limit=100", { credentials: "include", cache: "no-store", signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`Client Account returned ${response.status}`))))
      .then((payload) => {
        if (left) return;
        setLegacyRecords(Array.isArray(payload?.records) ? payload.records : Array.isArray(payload?.data?.records) ? payload.data.records : []);
        setLegacyState("ready");
      })
      .catch((err) => {
        if (left) return;
        console.warn("Old Client Account records could not be loaded:", err);
        setLegacyState("error");
      })
      .finally(() => clearTimeout(timer));
    return () => { left = true; clearTimeout(timer); controller.abort(); };
  }, [isAdmin]);

  const pendingKeys = useMemo(() => new Set(pending.map((item) => item.candidateKey).filter(Boolean)), [pending]);
  const emailCandidates = useMemo(
    () => emailDisbursementCandidates(emailItems, data).filter((candidate) => !pendingKeys.has(candidate.key)),
    [emailItems, data, pendingKeys],
  );
  const legacyCandidates = useMemo(
    () => legacyDisbursementCandidates(legacyRecords, brandType, data).filter((candidate) => !pendingKeys.has(candidate.key)),
    [legacyRecords, brandType, data, pendingKeys],
  );
  const emailInvoices = emailCandidates.filter((candidate) => candidate.likelyInvoice);
  const emailOther = emailCandidates.filter((candidate) => !candidate.likelyInvoice);

  const save = async (apply, success) => {
    try {
      await change(apply);
      if (success) toast.success(success);
      return true;
    } catch (err) {
      toast.error(err.message || "Disbursements could not be saved.");
      return false;
    }
  };

  // ---- Upload ----

  const chooseFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setReading(true);
    const found = [];
    for (const file of files) {
      let invoices = [];
      if (isPdfFile(file)) {
        try {
          invoices = splitPdfIntoInvoices(await readPdfPages(file));
        } catch (err) {
          console.warn("Invoice text could not be read:", err);
        }
      }
      if (!invoices.length) invoices = [{ page: 1, pageCount: 1, type: "other" }];
      invoices.forEach((invoice, index) => {
        found.push({
          key: `${file.name}-${file.size}-${index}-${Math.random().toString(36).slice(2, 6)}`,
          file,
          fileName: file.name,
          source: "upload",
          include: true,
          readSomething: Boolean(invoice.amount || invoice.invoiceRef || invoice.clientName),
          status: "not_paid",
          reimburse: false,
          aidRef: "",
          note: "",
          typeOther: "",
          ...invoice,
        });
      });
    }
    // An invoice already on the list (same supplier and number) starts unticked.
    setPending((current) => [...current, ...found.map((item) => {
      const duplicate = findDuplicate(data.items, item);
      return duplicate ? { ...item, include: false } : item;
    })]);
    setReading(false);
  };

  const markDuplicates = (items) => items.map((item) => (findDuplicate(data.items, item) ? { ...item, include: false } : item));

  // An emailed invoice: its PDF is already stored, so it is read from there and not uploaded
  // again. What the email intake read (amount, invoice number) fills any gap.
  // Reads one emailed invoice into rows for the check step.
  const readEmailCandidate = async (candidate) => {
    let invoices = [];
    try {
      const bytes = await fetchStoredFile(candidate.fileUrl);
      invoices = splitPdfIntoInvoices(await readPdfPages(new File([bytes], candidate.fileName || "invoice.pdf", { type: "application/pdf" })));
    } catch (err) {
      console.warn("Emailed invoice could not be read:", err);
    }
    if (!invoices.length) invoices = [{ page: 1, pageCount: 1, type: "other" }];
    const sharedPages = invoices.length > 1;
    const found = invoices.map((invoice, index) => ({
      status: "not_paid", reimburse: false, aidRef: "", note: "", typeOther: "",
      ...invoice,
      key: `${candidate.key}-${index}`,
      candidateKey: candidate.key,
      fileName: candidate.fileName,
      fileUrl: candidate.fileUrl,
      filePath: candidate.filePath,
      page: sharedPages ? invoice.page : null,
      source: "email",
      emailIds: candidate.emailIds,
      include: true,
      readSomething: Boolean(invoice.amount || invoice.invoiceRef || invoice.clientName),
      amount: invoice.amount || (sharedPages ? "" : candidate.amount),
      invoiceRef: invoice.invoiceRef || (sharedPages ? "" : candidate.invoiceRef),
      clientName: invoice.clientName || candidate.clientName,
      note: `Emailed ${day(candidate.receivedAt)}: ${candidate.subject}`.slice(0, 500),
    }));
    return found;
  };

  const bringInEmail = async (candidate) => {
    setBringingIn(candidate.key);
    const found = await readEmailCandidate(candidate);
    setPending((current) => [...current, ...markDuplicates(found)]);
    setUploadOpen(true);
    setBringingIn("");
  };

  // Every ticked email, read one after another (the PDF reader works on one file at a time),
  // then put into the check step together.
  const bringInSelected = async (candidates) => {
    if (!candidates.length) return;
    setBringingIn("selected");
    const found = [];
    for (let index = 0; index < candidates.length; index += 1) {
      setReadingProgress(`Reading ${index + 1} of ${candidates.length}…`);
      found.push(...await readEmailCandidate(candidates[index]));
    }
    setPending((current) => [...current, ...markDuplicates(found)]);
    setSelectedEmails(new Set());
    setReadingProgress("");
    setUploadOpen(true);
    setBringingIn("");
  };

  const toggleEmail = (key) => setSelectedEmails((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  // Old Client Account rows are already typed out, so they only need checking.
  const bringInLegacy = (candidates) => {
    const found = candidates.map((candidate) => ({
      reimburse: false, aidRef: "", typeOther: "", description: "", gst: "",
      ...candidate,
      key: candidate.key,
      candidateKey: candidate.key,
      fileName: "",
      source: "client_account",
      include: true,
      readSomething: true,
      note: [candidate.note, candidate.statementLabel && `From the Client Account: ${candidate.statementLabel}`].filter(Boolean).join(" · ").slice(0, 500),
    }));
    setPending((current) => [...current, ...markDuplicates(found)]);
    setUploadOpen(true);
  };

  const ignoreCandidates = (keys, label) =>
    save((current) => setIgnored(current, keys), label);

  const patchPending = (key, patch) =>
    setPending((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  const cancelUpload = () => {
    setPending([]);
    setUploadOpen(false);
  };

  const saveUpload = async () => {
    const ticked = pending.filter((item) => item.include);
    if (!ticked.length) return toast.error("Tick at least one invoice to save.");
    // The complete ones can be saved while the rest are finished: a batch brought in from
    // email should not wait on one invoice that could not be read.
    const chosen = ticked.filter(isComplete);
    const unfinished = ticked.filter((item) => !isComplete(item));
    if (!chosen.length) {
      const first = unfinished[0];
      return toast.error(`Enter the client and amount first (${first.fileName || first.supplier || "Client Account record"}${first.page > 1 ? `, page ${first.page}` : ""}).`);
    }
    if (unfinished.length && !window.confirm(
      `${chosen.length} invoice${chosen.length === 1 ? " is" : "s are"} ready. Save ${chosen.length === 1 ? "it" : "them"} now?\n\n`
      + `The ${unfinished.length} still missing a client or amount will stay here, marked in red, to finish.`,
    )) return;
    setSavingUpload(true);
    try {
      // Each new file is stored once, however many invoices it holds. An emailed PDF or an
      // old Client Account row has nothing to upload.
      const uploads = new Map();
      for (const item of chosen) {
        if (!item.file || uploads.has(item.file)) continue;
        const upload = await base44.integrations.Core.UploadFile({ file: item.file });
        uploads.set(item.file, upload || {});
      }
      const items = chosen.map((item) => {
        if (!item.file) return buildDisbursement(item, { by: userName });
        const upload = uploads.get(item.file);
        // The page matters only when the file holds more than one invoice.
        const shared = pending.filter((other) => other.file === item.file).length > 1;
        return buildDisbursement({
          ...item,
          page: shared ? item.page : null,
          fileName: item.file.name,
          fileUrl: upload?.file_url,
          filePath: upload?.storage_path,
        }, { by: userName });
      });
      if (await save((current) => addDisbursements(current, items), `${items.length} disbursement${items.length === 1 ? "" : "s"} saved.`)) {
        // File the emails as disbursements, so the Fee Slips screen stops listing them under
        // "check type". The invoice is saved either way; this only tidies that list.
        const emailIds = [...new Set(items.flatMap((item) => item.emailIds || []))];
        const refiled = await Promise.allSettled(emailIds.map((id) => updateEmailIntake(id, { kind: "disbursement" })));
        if (refiled.some((result) => result.status === "rejected")) {
          toast.warning("Saved, but some emails are still listed under \"check type\" on the Fee Slips screen.");
        }
        if (emailIds.length) {
          setEmailItems((current) => current.map((email) => (emailIds.includes(email.id) ? { ...email, kind: "disbursement" } : email)));
        }
        if (unfinished.length) {
          const saved = new Set(chosen.map((item) => item.key));
          setPending((current) => current.filter((item) => !saved.has(item.key)));
        } else {
          cancelUpload();
        }
      }
    } catch (err) {
      toast.error(err.message || "The invoice could not be uploaded.");
    } finally {
      setSavingUpload(false);
    }
  };

  // ---- The list ----

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return data.items
      .filter((item) => {
        // "Not yet paid" and "Paid" go by isPaidDisbursement, so a "VALS to be reimbursed"
        // invoice is with the paid ones whatever its own status.
        if (statusFilter === "open" && isPaidDisbursement(item)) return false;
        if (statusFilter === "paid" && !isPaidDisbursement(item)) return false;
        // Claimed in ATLAS and still to be paid on to the provider (the alert above the list).
        if (statusFilter === "pay_provider" && !awaitingProviderPayment([item]).length) return false;
        if (!["open", "all", "paid", "pay_provider"].includes(statusFilter) && item.status !== statusFilter) return false;
        if (typeFilter !== "all" && item.type !== typeFilter) return false;
        if (reimburseOnly && !item.reimburse) return false;
        if (needle) {
          const hay = [item.clientName, item.supplier, item.invoiceRef, item.description, item.aidRef, item.note, disbursementTypeLabel(item)]
            .join(" ").toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      })
      .sort((a, b) => String(b.invoiceDate || b.uploadedAt).localeCompare(String(a.invoiceDate || a.uploadedAt)));
  }, [data.items, statusFilter, typeFilter, reimburseOnly, search]);
  const visibleTotal = visible.reduce((sum, item) => sum + parseAmount(item.amount), 0);

  const setField = (item, fields, success) =>
    save((current) => updateDisbursement(current, item.id, fields, { by: userName }), success);

  const saveNote = (item) => {
    const note = noteDrafts[item.id];
    if (note === undefined || note === (item.note || "")) return;
    setField(item, { note }).then((ok) => {
      if (ok) setNoteDrafts(({ [item.id]: _saved, ...rest }) => rest);
    });
  };

  const startEdit = (item) => setEditing({
    id: item.id,
    file: null,
    values: Object.fromEntries([...EDIT_KEYS.map((key) => [key, item[key] === 0 ? "" : String(item[key] ?? "")]), ["reimburse", item.reimburse]]),
  });

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editing) return;
    const { id, values, file } = editing;
    if (!String(values.clientName || "").trim()) return toast.error("Enter the client's name.");
    if (!(parseAmount(values.amount) > 0)) return toast.error("Enter the invoice amount.");
    setSavingEdit(true);
    try {
      let fileFields = {};
      if (file) {
        const upload = await base44.integrations.Core.UploadFile({ file });
        fileFields = { fileName: file.name, fileUrl: upload?.file_url, filePath: upload?.storage_path, page: null };
      }
      const ok = await save(
        (current) => updateDisbursement(current, id, { ...values, ...fileFields }, { by: userName }),
        "Disbursement updated.",
      );
      if (ok) setEditing(null);
    } catch (err) {
      toast.error(err.message || "The new file could not be uploaded.");
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = (item) => {
    if (!window.confirm(`Remove the ${disbursementTypeLabel(item).toLowerCase()} invoice for ${item.clientName} (${money(item.amount)})?`)) return;
    save((current) => removeDisbursement(current, item.id), "Disbursement removed.");
  };

  // ---- Printing ----

  const printWindow = (title, body) => {
    const win = window.open("", "_blank");
    if (!win) return toast.error("Allow pop-ups to print.");
    win.document.write(`<!doctype html><html><head><title>${escapePrintHtml(title)}</title><style>
      body{font-family:Arial,sans-serif;font-size:11px;color:#0f172a;margin:16mm}
      h1{font-size:16px;margin:0 0 4px}h2{font-size:12px;margin:16px 0 4px}h2 span{font-weight:normal;color:#475569}
      table{width:100%;border-collapse:collapse}th,td{border:1px solid #cbd5e1;padding:4px 6px;text-align:left;vertical-align:top}
      th{background:#f1f5f9}.r{text-align:right}.total{margin-top:14px;font-weight:bold;font-size:12px}
    </style></head><body>${body}<script>window.addEventListener("load",()=>window.print());<\/script></body></html>`);
    win.document.close();
  };

  const tableOf = (items) => `<table><thead><tr><th>Client</th><th>Type</th><th>Supplier</th><th>Invoice</th><th>Date</th><th>Aid / file no.</th><th>Description</th><th class="r">Amount</th></tr></thead><tbody>${
    items.map((item) => `<tr><td>${escapePrintHtml(item.clientName)}</td><td>${escapePrintHtml(disbursementTypeLabel(item))}</td>
      <td>${escapePrintHtml(item.supplier)}</td><td>${escapePrintHtml(item.invoiceRef)}</td><td>${escapePrintHtml(day(item.invoiceDate))}</td>
      <td>${escapePrintHtml(item.aidRef)}</td><td>${escapePrintHtml(item.description)}</td><td class="r">${escapePrintHtml(money(item.amount))}</td></tr>`).join("")
  }</tbody></table>`;

  const printList = () => {
    const groups = DISBURSEMENT_STATUSES
      .map((status) => ({ ...status, items: visible.filter((item) => item.status === status.value) }))
      .filter((group) => group.items.length)
      .map((group) => `<h2>${escapePrintHtml(group.label)} <span>${group.items.length} &middot; ${escapePrintHtml(money(group.items.reduce((s, i) => s + parseAmount(i.amount), 0)))}</span></h2>${tableOf(group.items)}`)
      .join("");
    printWindow(`${brand.prefix} - Disbursements`, `
      <h1>${escapePrintHtml(brand.prefix)} &mdash; Disbursements</h1>
      <div>Printed ${escapePrintHtml(new Date().toLocaleString("en-AU"))}</div>
      ${groups || "<p>No disbursements match.</p>"}
      <div class="total">Total: ${escapePrintHtml(money(visibleTotal))} (${visible.length} invoice${visible.length === 1 ? "" : "s"})</div>`);
  };

  // The claim that goes to VALS: every invoice ticked, whatever the filters, by supplier.
  const printReimbursement = () => {
    const marked = data.items.filter((item) => item.reimburse);
    if (!marked.length) return toast.error(`No disbursements are marked "${REIMBURSEMENT_LABEL}" yet.`);
    const bySupplier = new Map();
    for (const item of marked) {
      const key = item.supplier || "Supplier not given";
      bySupplier.set(key, [...(bySupplier.get(key) || []), item]);
    }
    const groups = [...bySupplier.entries()].sort(([a], [b]) => a.localeCompare(b))
      .map(([supplier, items]) => `<h2>${escapePrintHtml(supplier)} <span>${items.length} &middot; ${escapePrintHtml(money(items.reduce((s, i) => s + parseAmount(i.amount), 0)))}</span></h2>${tableOf(items)}`)
      .join("");
    printWindow(`${brand.prefix} - ${REIMBURSEMENT_LABEL}`, `
      <h1>${escapePrintHtml(brand.prefix)} &mdash; Disbursements: ${escapePrintHtml(REIMBURSEMENT_LABEL)}</h1>
      <div>Printed ${escapePrintHtml(new Date().toLocaleString("en-AU"))}</div>
      ${groups}
      <div class="total">Total to be reimbursed: ${escapePrintHtml(money(summary.reimburse.total))} (${marked.length} invoice${marked.length === 1 ? "" : "s"})</div>`);
  };

  const card = (active, onClick, label, value, sub, extra = "") => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border p-4 text-left shadow-sm ${active ? "border-slate-800 ring-2 ring-slate-700" : "border-slate-200 hover:border-slate-300"} ${extra || "bg-white"}`}
    >
      <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="mt-1 block text-lg font-bold text-slate-900">{value}</span>
      <span className="block text-[11px] text-slate-500">{sub}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-slate-850 p-6 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
          <div className="space-y-1">
            <h1 className="text-xl font-bold tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
              <Receipt className={`w-6 h-6 ${brand.icon}`} />
              {brand.prefix} Disbursements
            </h1>
            <p className="text-xs text-slate-500">
              Psychological, psychiatric and medical reports, interpreters, transcripts and other outside invoices for {brand.orgName}: upload the invoice, then track it to paid.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button type="button" variant="outline" onClick={printList} className="h-9 gap-1 px-3 text-xs">
              <Printer className="h-4 w-4" /> Print list
            </Button>
            {reimbursement && (
              <Button type="button" variant="outline" onClick={printReimbursement} className="h-9 gap-1 px-3 text-xs border-amber-300 text-amber-800 hover:bg-amber-50">
                <Printer className="h-4 w-4" /> Print {REIMBURSEMENT_LABEL}
              </Button>
            )}
            {!readOnly && (
              <Button
                type="button"
                onClick={() => setUploadOpen(true)}
                disabled={!loaded || loadError}
                className={`h-9 gap-1 px-3 text-xs text-white ${brand.button}`}
              >
                <FileUp className="h-4 w-4" /> Upload invoices
              </Button>
            )}
          </div>
        </div>

        {loadError && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            Disbursements could not be loaded. Nothing can be changed until they load; reload the page to try again.
          </p>
        )}

        {payProvider.length > 0 && !readOnly && (
          // Claimed through ATLAS: VLA pays the firm, so the provider still has to be paid
          // (Emma, 30 Oct 2026).
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 print:hidden">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{payProvider.length} claimed through ATLAS</strong> ({money(payProvider.reduce((sum, item) => sum + (Number(item.amount) || 0), 0))}) still to be paid to the provider.
            </span>
            <Button type="button" variant="outline" onClick={() => { setStatusFilter("pay_provider"); setReimburseOnly(false); }} className="h-7 border-red-300 px-3 text-xs text-red-800 hover:bg-red-100">
              Show them
            </Button>
          </div>
        )}

        <div className={`grid grid-cols-2 gap-3 ${reimbursement ? "lg:grid-cols-4" : "lg:grid-cols-3"} print:hidden`}>
          {card(statusFilter === "open" && !reimburseOnly, () => { setStatusFilter("open"); setReimburseOnly(false); },
            "Outstanding", money(summary.open.total), `${summary.open.count} not yet paid`)}
          {card(statusFilter === "paid" && !reimburseOnly, () => { setStatusFilter("paid"); setReimburseOnly(false); },
            "Paid", money(summary.paid.total), `${summary.paid.count} invoice${summary.paid.count === 1 ? "" : "s"}`)}
          {reimbursement && card(reimburseOnly, () => { setStatusFilter("all"); setReimburseOnly(true); },
            REIMBURSEMENT_LABEL, money(summary.reimburse.total), `${summary.reimburse.count} marked`,
            summary.reimburse.count ? "bg-amber-50 border-amber-200" : "")}
          {card(statusFilter === "all" && !reimburseOnly, () => { setStatusFilter("all"); setReimburseOnly(false); },
            "All disbursements", money(summary.all.total), `${summary.all.count} invoice${summary.all.count === 1 ? "" : "s"}`)}
        </div>

        {isAdmin && (emailState !== "idle" || legacyState !== "idle") && (emailInvoices.length > 0 || emailOther.length > 0 || legacyCandidates.length > 0 || emailState === "loading" || legacyState === "loading" || emailState === "error" || legacyState === "error") && (
          <Card className="shadow-sm border-amber-200 print:hidden">
            <CardHeader className="py-4 border-b border-amber-100 bg-amber-50/40">
              <CardTitle className="text-sm font-bold text-slate-800">To bring in</CardTitle>
              <CardDescription className="text-xs text-slate-600">
                Disbursement invoices that arrived by email, and the old Client Account&apos;s records, not yet on this list.
                Add each one (you check it before it is saved) or mark it not a disbursement.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                    <Mail className="h-4 w-4" /> From email
                    {emailState === "ready" && <span className="font-normal text-slate-500">· {emailInvoices.length} invoice{emailInvoices.length === 1 ? "" : "s"}</span>}
                  </h3>
                  {emailOther.length > 0 && (
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <input type="checkbox" checked={showOtherFiles} onChange={(event) => setShowOtherFiles(event.target.checked)} className="h-3.5 w-3.5" />
                      Also show {emailOther.length} other attachment{emailOther.length === 1 ? "" : "s"} (quotes, letters, reports)
                    </label>
                  )}
                </div>
                {(() => {
                  const listed = showOtherFiles ? [...emailInvoices, ...emailOther] : emailInvoices;
                  const ticked = listed.filter((candidate) => selectedEmails.has(candidate.key));
                  if (!listed.length) return null;
                  const allTicked = ticked.length === listed.length;
                  return (
                    <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                      <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-700">
                        <input
                          type="checkbox"
                          checked={allTicked}
                          ref={(box) => { if (box) box.indeterminate = ticked.length > 0 && !allTicked; }}
                          onChange={() => setSelectedEmails(allTicked ? new Set() : new Set(listed.map((candidate) => candidate.key)))}
                          disabled={Boolean(bringingIn)}
                          className="h-4 w-4"
                          aria-label="Select all emailed invoices"
                        />
                        Select all ({listed.length})
                      </label>
                      <Button
                        type="button"
                        onClick={() => bringInSelected(ticked)}
                        disabled={!ticked.length || Boolean(bringingIn) || saving || loadError}
                        className={`h-7 px-3 text-[11px] text-white ${brand.button}`}
                      >
                        {bringingIn === "selected" ? readingProgress || "Reading…" : `Add selected (${ticked.length})`}
                      </Button>
                      {ticked.length > 0 && !bringingIn && (
                        <button type="button" onClick={() => setSelectedEmails(new Set())} className="text-[11px] text-slate-500 underline">Clear</button>
                      )}
                    </div>
                  );
                })()}
                {emailState === "loading" && <p className="text-[11px] text-slate-500">Looking for emailed invoices…</p>}
                {emailState === "error" && (
                  <p className="text-[11px] text-red-700">
                    Emailed invoices could not be loaded. <button type="button" onClick={loadEmails} className="underline">Try again</button>
                  </p>
                )}
                {emailState === "ready" && emailInvoices.length === 0 && !showOtherFiles && (
                  <p className="text-[11px] text-slate-500">No emailed invoices waiting.</p>
                )}
                {(showOtherFiles ? [...emailInvoices, ...emailOther] : emailInvoices).length > 0 && (
                  <div className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                    {(showOtherFiles ? [...emailInvoices, ...emailOther] : emailInvoices).map((candidate) => (
                      <div key={candidate.key} className="grid gap-2 p-2.5 odd:bg-white even:bg-slate-50 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center">
                        <input
                          type="checkbox"
                          checked={selectedEmails.has(candidate.key)}
                          onChange={() => toggleEmail(candidate.key)}
                          disabled={Boolean(bringingIn)}
                          aria-label={`Select ${candidate.fileName}`}
                          className="h-4 w-4"
                        />
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-semibold text-slate-800">{candidate.fileName}</span>
                            {candidate.amount && <span className="font-semibold text-slate-700">{money(candidate.amount)}</span>}
                            {!candidate.likelyInvoice && (
                              <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-500">Not an invoice?</span>
                            )}
                            {candidate.copies > 1 && <span className="text-[10px] text-slate-500">emailed {candidate.copies} times</span>}
                          </p>
                          <p className="truncate text-[11px] text-slate-500">
                            {day(candidate.receivedAt)} · {senderName(candidate.sender)} · {candidate.subject}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <a
                            href={candidate.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 px-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            <Download className="h-3.5 w-3.5" /> PDF
                          </a>
                          <Button
                            type="button"
                            onClick={() => bringInEmail(candidate)}
                            disabled={Boolean(bringingIn) || saving || loadError}
                            className={`h-8 px-3 text-[11px] text-white ${brand.button}`}
                          >
                            {bringingIn === candidate.key ? "Reading…" : "Add"}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => ignoreCandidates([candidate.key], "Won't be offered again.")}
                            disabled={saving || loadError}
                            title="Not a disbursement - stop offering this email"
                            aria-label={`Not a disbursement: ${candidate.fileName}`}
                            className="h-8 px-2 text-[11px] text-slate-500 hover:text-slate-800"
                          >
                            <Archive className="h-3.5 w-3.5" /> Not one
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {legacyState === "error" && (
                <p className="text-[11px] text-slate-500">
                  The old Client Account (Deadline Guard) could not be reached, so its records are not shown. Reload the page later to try again.
                </p>
              )}

              {(legacyCandidates.length > 0 || legacyState === "loading") && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                      <Archive className="h-4 w-4" /> From the old Client Account
                      {legacyState === "ready" && <span className="font-normal text-slate-500">· {legacyCandidates.length} record{legacyCandidates.length === 1 ? "" : "s"}</span>}
                    </h3>
                    {legacyCandidates.length > 1 && (
                      <Button type="button" variant="outline" onClick={() => bringInLegacy(legacyCandidates)} disabled={saving || loadError} className="h-7 px-2.5 text-[11px]">
                        Add all {legacyCandidates.length}
                      </Button>
                    )}
                  </div>
                  {legacyState === "loading" && <p className="text-[11px] text-slate-500">Asking Deadline Guard for the Client Account&apos;s records (this can take a while)…</p>}
                  {legacyCandidates.length > 0 && (
                    <div className="max-h-[320px] divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                      {legacyCandidates.map((candidate) => (
                        <div key={candidate.key} className="grid gap-2 p-2.5 odd:bg-white even:bg-slate-50 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                          <div className="min-w-0">
                            <p className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="font-semibold text-slate-800">{candidate.clientName || "Client not given"}</span>
                              {candidate.amount && <span className="font-semibold text-slate-700">{money(candidate.amount)}</span>}
                              <span className="text-[10px] text-slate-500">{disbursementStatusLabel(candidate.status)}</span>
                            </p>
                            <p className="truncate text-[11px] text-slate-500">
                              {[candidate.supplier, candidate.invoiceRef && `Invoice ${candidate.invoiceRef}`, day(candidate.invoiceDate)].filter(Boolean).join(" · ")}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button type="button" onClick={() => bringInLegacy([candidate])} disabled={saving || loadError} className={`h-8 px-3 text-[11px] text-white ${brand.button}`}>
                              Add
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => ignoreCandidates([candidate.key], "Won't be offered again.")}
                              disabled={saving || loadError}
                              aria-label={`Not a disbursement: ${candidate.clientName}`}
                              className="h-8 px-2 text-[11px] text-slate-500 hover:text-slate-800"
                            >
                              <Archive className="h-3.5 w-3.5" /> Not one
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {uploadOpen && (
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="py-4 border-b border-slate-100">
              <CardTitle className="text-sm font-bold text-slate-800">Check and save invoices</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Choose one or more PDFs. A PDF holding several invoices, one to a page, is split into one invoice per page.
                Details are read from the PDF where possible - open the PDF to check each one before saving.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <Input
                type="file"
                multiple
                accept=".pdf,application/pdf,image/*"
                aria-label="Invoice files"
                onChange={(event) => { chooseFiles(event.target.files); event.target.value = ""; }}
                className="h-9 text-xs bg-white"
              />
              {reading && <p className="text-[11px] text-slate-500">Reading the invoices…</p>}
              {pending.length > 0 && (
                <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900">
                  {pending.length} invoice{pending.length === 1 ? " is" : "s are"} waiting here and not on your list yet.
                  The Outstanding and Paid totals above update once {pending.length === 1 ? "it is" : "they are"} saved.
                  {(() => {
                    const unfinished = pending.filter((item) => item.include && !isComplete(item)).length;
                    return unfinished > 0 ? ` ${unfinished} still need${unfinished === 1 ? "s" : ""} a client or amount (marked in red).` : "";
                  })()}
                </p>
              )}

              {pending.map((item, index) => {
                const duplicate = findDuplicate(data.items, item);
                return (
                  <div key={item.key} className={`space-y-3 rounded-lg border border-l-4 p-3 shadow-sm ${!item.include ? "border-slate-200 border-l-slate-300 bg-slate-50 opacity-70" : isComplete(item) ? "border-slate-300 border-l-sky-500 bg-white" : "border-red-200 border-l-red-500 bg-white"}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <label className="flex items-center gap-2 text-xs font-bold text-slate-800">
                        <input
                          type="checkbox"
                          checked={item.include}
                          onChange={(event) => patchPending(item.key, { include: event.target.checked })}
                          className="h-4 w-4"
                        />
                        Invoice {index + 1}: {item.source === "client_account" ? `Client Account record${item.supplier ? ` · ${item.supplier}` : ""}` : item.fileName}
                        {item.page && (item.file ? pending.filter((other) => other.file === item.file).length > 1 : true) ? ` · page ${item.page}` : ""}
                        {item.source === "email" && <span className="ml-1 font-normal text-slate-500">(from email)</span>}
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        {!item.readSomething && (
                          <span className="text-[11px] text-amber-700">Nothing could be read from this file (it may be a scan). Type the details in.</span>
                        )}
                        {item.include && !isComplete(item) && (
                          <span className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-700">Needs a client and amount</span>
                        )}
                        {pendingFileLink(item) && (
                          <a
                            href={pendingFileLink(item)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                            title={item.fileName}
                          >
                            <Download className="h-3.5 w-3.5" /> Open PDF{item.page > 1 ? ` (page ${item.page})` : ""}
                          </a>
                        )}
                      </div>
                    </div>
                    {duplicate && (
                      <p className="flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        Already on the list: {duplicate.supplier} invoice {duplicate.invoiceRef} for {duplicate.clientName} ({money(duplicate.amount)}). Left unticked.
                      </p>
                    )}
                    {item.include && (
                      <InvoiceFields
                        brand={brandType}
                        idPrefix={`disb-new-${index}`}
                        values={item}
                        onChange={(patch) => patchPending(item.key, patch)}
                        reimbursement={reimbursement}
                      />
                    )}
                  </div>
                );
              })}

              <div className="flex gap-2">
                {pending.length > 0 && (
                  <Button type="button" onClick={saveUpload} disabled={savingUpload || saving || reading} className={`h-8 px-4 text-[11px] text-white ${brand.button}`}>
                    {savingUpload ? "Saving…" : (() => {
                      const ready = pending.filter((item) => item.include && isComplete(item)).length;
                      const ticked = pending.filter((item) => item.include).length;
                      return ready === ticked
                        ? `Save ${ticked} invoice${ticked === 1 ? "" : "s"}`
                        : `Save ${ready} ready (of ${ticked})`;
                    })()}
                  </Button>
                )}
                <Button type="button" variant="ghost" onClick={cancelUpload} className="h-8 px-3 text-[11px]">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="print:hidden">
          <ClaimReceiptsFolder
            store={claimReceipts}
            candidates={claimCandidates}
            itemWord="disbursement"
            brand={brandType}
            canEdit={isAdmin}
            userName={userName}
          />
        </div>

        <Card className="shadow-sm border-slate-200">
          <CardHeader className="py-4 border-b border-slate-100 space-y-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1">
                <Label htmlFor="disb-search" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Search</Label>
                <Input
                  id="disb-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Client, supplier, invoice or aid number…"
                  className="mt-1 h-8 text-xs"
                />
              </div>
              <div className="md:w-56">
                <Label className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="mt-1 h-8 text-xs" aria-label="Filter by status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open" className="text-xs">Not yet paid</SelectItem>
                    <SelectItem value="all" className="text-xs">All statuses</SelectItem>
                    <SelectItem value="pay_provider" className="text-xs">Claimed in ATLAS - pay the provider</SelectItem>
                    {statusesFor(brandType).map((status) => (
                      <SelectItem key={status.value} value={status.value} className="text-xs">{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:w-56">
                <Label className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="mt-1 h-8 text-xs" aria-label="Filter by type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All types</SelectItem>
                    {DISBURSEMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-xs">{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {reimbursement && (
                <label className="flex items-center gap-2 pb-1.5 text-xs font-semibold text-amber-800">
                  <input type="checkbox" checked={reimburseOnly} onChange={(event) => setReimburseOnly(event.target.checked)} className="h-4 w-4 accent-amber-600" />
                  {REIMBURSEMENT_LABEL} only
                </label>
              )}
            </div>
            <p className="text-[11px] text-slate-500">
              Showing {visible.length} of {data.items.length} · {money(visibleTotal)}
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {!loaded ? (
              <p className="p-6 text-xs text-slate-400">Loading…</p>
            ) : visible.length === 0 ? (
              <p className="p-8 text-center text-xs text-slate-400">
                {data.items.length === 0 ? "No disbursements yet. Upload a supplier's invoice to start." : "No disbursements match."}
              </p>
            ) : (
              <div className="space-y-3 bg-slate-50 p-3">
                {visible.map((item) => {
                  const link = fileLink(item);
                  return (
                    <div
                      key={item.id}
                      data-focus-id={item.id}
                      className={`overflow-hidden rounded-lg border border-l-4 border-slate-200 bg-white shadow-sm ${STATUS_EDGE[item.status] || STATUS_EDGE.not_paid}`}
                    >
                      <div className="grid gap-2 p-3 md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_auto] md:items-center">
                        <div className="min-w-0 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">{item.clientName || "Unnamed client"}</span>
                            <span className="text-sm font-semibold text-slate-700">{money(item.amount)}</span>
                            <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">
                              {disbursementTypeLabel(item)}
                            </span>
                            {item.reimburse && item.status !== "paid" && (
                              <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-800" title="Ticked VALS to be reimbursed: paid from the crim budget, outside ATLAS">
                                Paid - crim budget
                              </span>
                            )}
                            {item.financeEmailedAt && (
                              <span className="text-[10px] text-indigo-700" title={`To ${item.financeEmailedTo}`}>Request sent to Finance {day(item.financeEmailedAt)}</span>
                            )}
                            {/* The receipt attached with a finance email; not shown again when the same
                                file is in the Claim receipts folder, which lists it below. */}
                            {item.claimReceiptUrl && statementFileUrl({ fileUrl: item.claimReceiptUrl })
                              && !receiptsFor(claimReceipts.data, "disbursement", item.id).some((receipt) => receipt.fileUrl === item.claimReceiptUrl) && (
                              <a
                                href={statementFileUrl({ fileUrl: item.claimReceiptUrl })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-semibold text-indigo-700 underline"
                                title={item.claimReceiptName}
                              >
                                Claim receipt
                              </a>
                            )}
                            {receiptsFor(claimReceipts.data, "disbursement", item.id).map((receipt) => (
                              <a
                                key={receipt.id}
                                href={statementFileUrl({ fileUrl: receipt.fileUrl }) || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-semibold text-indigo-700 underline"
                                title={`ATLAS claim ${receipt.claimId || ""} ${receipt.claimRef ? `(${receipt.claimRef})` : ""}`.trim()}
                              >
                                Claim receipt{receipt.claimId ? ` ${receipt.claimId}` : ""}
                              </a>
                            ))}
                            {item.status === "partial_paid" && (item.vlaClaimed !== null && item.vlaClaimed !== undefined ? (
                              <span className="text-[10px] text-violet-700">{money(item.vlaClaimed)} claimed from VLA, {money(amountOwing(item))} from budget</span>
                            ) : item.amountPaid > 0 && (
                              <span className="text-[10px] text-violet-700">{money(item.amountPaid)} paid, {money(amountOwing(item))} owing</span>
                            ))}
                          </div>
                          <p className="truncate text-[11px] text-slate-500">
                            {[item.supplier, item.invoiceRef && `Invoice ${item.invoiceRef}`, day(item.invoiceDate), item.description,
                              item.aidRef && `Aid/file ${item.aidRef}`, item.status === "paid" && item.paidOn && `paid ${day(item.paidOn)}`]
                              .filter(Boolean).join(" · ")}
                          </p>
                          {item.travelLines?.length > 0 && (
                            // A travel claim: each expense, what it is for, its fee and receipts.
                            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-slate-50 text-[11px]">
                              {item.travelLines.map((line, index) => (
                                <li key={index} className="flex flex-wrap items-baseline gap-x-2 px-2 py-1">
                                  <span className="font-semibold text-slate-800">{line.type}</span>
                                  {line.date && <span className="text-slate-500">{day(line.date)}</span>}
                                  {line.detail && <span className="text-slate-500">{line.detail}</span>}
                                  <span className="ml-auto font-semibold text-slate-800">{money(line.amount)}</span>
                                  <span className="flex w-full flex-wrap gap-x-3">
                                    {line.receipts?.length ? line.receipts.map((receipt, receiptIndex) => (
                                      <a key={receiptIndex} href={receipt.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-violet-700 hover:underline">
                                        <Paperclip className="h-3 w-3" />{receipt.name}
                                      </a>
                                    )) : <span className="text-slate-400">No receipt attached</span>}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {readOnly ? (
                            item.note && <p className="text-[11px] text-slate-600">Note: {item.note}</p>
                          ) : (
                            <Input
                              value={noteDrafts[item.id] ?? item.note ?? ""}
                              onChange={(event) => setNoteDrafts((current) => ({ ...current, [item.id]: event.target.value }))}
                              onBlur={() => saveNote(item)}
                              placeholder="Note"
                              aria-label={`Note for ${item.clientName}`}
                              className="h-7 text-[11px]"
                            />
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {readOnly ? (
                            <span className={`inline-flex h-8 items-center rounded-md border px-2 text-[11px] font-semibold ${STATUS_STYLE[item.status] || ""}`}>
                              {disbursementStatusText(item)}{item.status === "paid" && item.paidOn ? ` ${day(item.paidOn)}` : ""}
                            </span>
                          ) : (
                            <StatusSelect
                              value={item.status}
                              brand={brandType}
                              item={item}
                              onChange={(status) => setField(item, { status })}
                              disabled={saving}
                              label={`Status for ${item.clientName}`}
                            />
                          )}
                          {reimbursement && !readOnly && (
                            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
                              <input
                                type="checkbox"
                                checked={item.reimburse}
                                disabled={saving}
                                onChange={(event) => setField(item, { reimburse: event.target.checked })}
                                className="h-3.5 w-3.5 accent-amber-600"
                              />
                              {REIMBURSEMENT_LABEL}
                            </label>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {link && (
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={item.fileName}
                              className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 px-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              <Download className="h-3.5 w-3.5" /> PDF{item.page > 1 ? ` p.${item.page}` : ""}
                            </a>
                          )}
                          {emailsFinance && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setEmailingId(emailingId === item.id ? null : item.id)}
                              disabled={saving}
                              aria-expanded={emailingId === item.id}
                              title="Email VALS Finance that this has been claimed in ATLAS and can be paid"
                              className="h-8 gap-1 px-2 text-[11px] text-indigo-800 border-indigo-200 hover:bg-indigo-50"
                            >
                              <Mail className="h-3.5 w-3.5" /> Email finance
                            </Button>
                          )}
                          {!readOnly && (<>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => (editing?.id === item.id ? setEditing(null) : startEdit(item))}
                            disabled={saving || savingEdit}
                            aria-label={`Edit the invoice for ${item.clientName}`}
                            aria-expanded={editing?.id === item.id}
                            className="h-8 px-2 text-slate-500 hover:text-slate-800"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => remove(item)}
                            disabled={saving}
                            aria-label={`Remove the invoice for ${item.clientName}`}
                            className="h-8 px-2 text-slate-500 hover:text-rose-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          </>)}
                        </div>
                      </div>
                      {item.status === "partial_paid" && editing?.id !== item.id && (
                        <div className="px-3 pb-3">
                          <PartialPaidSplit
                            idPrefix={`split-${item.id}`}
                            total={item.amount}
                            vlaClaimed={item.vlaClaimed}
                            fromBudget={item.budgetRemaining}
                            disabled={saving || readOnly}
                            onCommit={(split) => setField(item, { vlaClaimed: split.vlaClaimed, budgetRemaining: split.fromBudget })}
                          />
                        </div>
                      )}
                      {emailsFinance && emailingId === item.id && (
                        <FinanceEmailPanel
                          item={item}
                          userName={userName}
                          signOff={signOff}
                          buttonClass={brand.button}
                          onClose={() => setEmailingId(null)}
                          folderReceipts={receiptsFor(claimReceipts.data, "disbursement", item.id)}
                          onReceiptToFolder={({ fields, file }) => claimReceipts.change((current) => addReceipt(
                            current,
                            buildReceipt(fields, file, { by: userName }),
                            claimCandidates.find((candidate) => candidate.id === item.id),
                          ))}
                          onSent={(fields) => {
                            setEmailingId(null);
                            setField(item, fields);
                          }}
                        />
                      )}
                      {editing?.id === item.id && (
                        <form onSubmit={saveEdit} className="space-y-3 border-t border-slate-100 bg-slate-50/70 p-3" aria-label={`Edit the invoice for ${item.clientName}`}>
                          <InvoiceFields
                        brand={brandType}
                            idPrefix="disb-edit"
                            values={editing.values}
                            onChange={(patch) => setEditing((current) => ({ ...current, values: { ...current.values, ...patch } }))}
                            reimbursement={reimbursement}
                          />
                          <div className="max-w-sm">
                            <Label htmlFor="disb-edit-file" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Replace the file (optional)</Label>
                            <Input
                              id="disb-edit-file"
                              type="file"
                              accept=".pdf,application/pdf,image/*"
                              onChange={(event) => setEditing((current) => ({ ...current, file: event.target.files?.[0] || null }))}
                              className="mt-1 h-9 text-xs bg-white"
                            />
                            {item.fileName && !editing.file && <p className="mt-1 truncate text-[10px] text-slate-500">Current: {item.fileName}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button type="submit" disabled={savingEdit || saving} className="h-8 px-4 text-[11px] bg-slate-800 text-white hover:bg-slate-900">
                              {savingEdit ? "Saving…" : "Save changes"}
                            </Button>
                            <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="h-8 px-3 text-[11px]">Cancel</Button>
                          </div>
                        </form>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
